/**
 * useBridgeFetchProgress — live bridge re-fetch lifecycle for ONE custom
 * print-file issue (BLU-880).
 *
 * After "Fetch & install" / "Self-Heal Now" requeues the bridge on a RIP
 * (.arx4) location, the middle of the pipeline used to be invisible: the
 * bridge polls (60s), downloads, RIPs, uploads, then resolves. This hook makes
 * each step visible by listening on `/events/print-files` for
 * `print_file_bridge_progress` (issueId match → state) and
 * `print_file_issue_resolved` (issueId match → `resolved`), with a 15s poll
 * of the issue read model as the fallback when the stream is down. The two
 * sources are merged with `pickLaterSnapshot` — whichever is ahead wins.
 *
 * Transport contract copied VERBATIM from `hooks/usePrintFileEvents.ts` (this
 * is how the repo does SSE):
 *  - manual exponential-backoff reconnect (NOT browser-native, which storms
 *    when offline),
 *  - recreates the EventSource on Supabase auth-token refresh,
 *  - fail-closed cleanup on unmount / disable.
 *
 * TIMEOUT: when the state has not advanced for `bridgeFetchStaleMs(state)`
 * (150 s while still `queued`, 2.5× the bridge's 60 s poll; 5 minutes once the
 * bridge picked it up, BLU-1309 F1b) the hook flags `timedOut` so the UI can offer
 * "Try again". It is a SOFT signal — the stream and the poll keep running,
 * and a later advance clears it. The watchdog is seeded from the SERVER
 * stamp (`initial.at`), never from mount time: a dialog reopened on a stamp
 * that is already older than the threshold opens timed-out straight away,
 * with the controls available, instead of trapping the operator behind a
 * fresh 150s wait. An absent/unparseable stamp on a seeded run counts as
 * already stale.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { trpc } from '@/lib/trpc';
import {
  BRIDGE_FETCH_POLL_MS,
  bridgeFetchStaleMs,
  inferFailedStep,
  isBridgeFetchUiState,
  isTerminalBridgeFetchState,
  pickLaterSnapshot,
  snapshotFromMetadata,
  type BridgeFetchSnapshot,
  type BridgeFetchState,
  type BridgeFetchStep,
} from '../bridgeFetchState';

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL || 'http://localhost:59321';
const RECONNECT_BASE_MS = 3_000; // Initial retry delay
const RECONNECT_MAX_MS = 60_000; // Max retry delay (1 minute)
const STALE_TICK_MS = 1_000;

type PrintFileEvent = {
  type: 'print_file_bridge_progress' | 'print_file_issue_resolved' | 'heartbeat' | string;
  organizationId?: string;
  locationId?: string;
  issueId?: string;
  state?: string;
  at?: string;
  reason?: string;
  resolvedAt?: string;
};

export interface UseBridgeFetchProgressOptions {
  /** Open the stream + poll only while a fetch is being tracked. */
  enabled: boolean;
  /** Seed from the issue row (dialog reopened on an in-flight issue). */
  initial?: BridgeFetchSnapshot | null;
  /** Fired ONCE per tracked run when the issue resolves. */
  onResolved?: () => void;
}

export interface UseBridgeFetchProgressResult {
  snapshot: BridgeFetchSnapshot | null;
  /** The stepper row a `failed` state paints red (null unless failed). */
  failedStep: BridgeFetchStep | null;
  /** No advance for `bridgeFetchStaleMs(state)` while non-terminal. */
  timedOut: boolean;
  /** Start (or restart) tracking from `queued` — call after a requeue. */
  reset: (initial?: BridgeFetchSnapshot | null) => void;
}

const OPTIMISTIC_QUEUED: BridgeFetchSnapshot = { state: 'queued', at: null, reason: null };

/**
 * The watchdog's "last advance" for a snapshot. No snapshot → now (nothing to
 * be stale about yet). A server stamp → its own time, so the staleness the
 * operator sees is measured from when the bridge last moved, not from when
 * the dialog opened; unparseable → 0 (already stale). A snapshot with NO
 * stamp is read two ways: on a SEED (a reopened run whose row carries no
 * usable timestamp) nothing says the bridge is alive → already stale; on a
 * RESET (the client-side optimistic `queued`) the requeue just happened → now.
 */
function seedAdvanceAt(
  snapshot: BridgeFetchSnapshot | null,
  now: number,
  mode: 'seed' | 'reset',
): number {
  if (!snapshot) return now;
  if (snapshot.at === null) return mode === 'reset' ? now : 0;
  const parsed = Date.parse(snapshot.at);
  return Number.isFinite(parsed) ? parsed : 0;
}

/** F1b: the threshold depends on the state (150 s before pickup, at least
 *  5 minutes after: see `bridgeFetchStaleMs`). */
function isStale(lastAdvanceAt: number, now: number, state: BridgeFetchSnapshot['state'] | undefined): boolean {
  return now - lastAdvanceAt >= bridgeFetchStaleMs(state);
}

export function useBridgeFetchProgress(
  issueId: string,
  locationId: string | null | undefined,
  { enabled, initial = null, onResolved }: UseBridgeFetchProgressOptions,
): UseBridgeFetchProgressResult {
  const utils = trpc.useUtils();

  const [snapshot, setSnapshot] = useState<BridgeFetchSnapshot | null>(initial);
  const snapshotRef = useRef<BridgeFetchSnapshot | null>(initial);
  const lastProgressRef = useRef<BridgeFetchState | null>(
    initial && initial.state !== 'failed' && initial.state !== 'resolved' ? initial.state : null,
  );
  const lastAdvanceAtRef = useRef(seedAdvanceAt(initial, Date.now(), 'seed'));
  // Seeded synchronously so a stale reopen never renders one frame of "active".
  const [timedOut, setTimedOut] = useState(() =>
    initial !== null &&
    !isTerminalBridgeFetchState(initial.state) &&
    isStale(lastAdvanceAtRef.current, Date.now(), initial.state),
  );
  const resolvedFiredRef = useRef(false);
  const onResolvedRef = useRef(onResolved);
  onResolvedRef.current = onResolved;
  const locationIdRef = useRef(locationId);
  locationIdRef.current = locationId;

  const apply = useCallback((incoming: BridgeFetchSnapshot) => {
    const merged = pickLaterSnapshot(snapshotRef.current, incoming);
    if (merged === snapshotRef.current) return;
    const advanced = merged.state !== snapshotRef.current?.state;
    snapshotRef.current = merged;
    setSnapshot(merged);
    if (merged.state !== 'failed' && merged.state !== 'resolved') {
      lastProgressRef.current = merged.state;
    }
    if (advanced) {
      lastAdvanceAtRef.current = Date.now();
      setTimedOut(false);
    }
    if (merged.state === 'resolved' && !resolvedFiredRef.current) {
      resolvedFiredRef.current = true;
      onResolvedRef.current?.();
    }
  }, []);

  const reset = useCallback(
    (next: BridgeFetchSnapshot | null = OPTIMISTIC_QUEUED) => {
      const now = Date.now();
      snapshotRef.current = next;
      lastProgressRef.current =
        next && next.state !== 'failed' && next.state !== 'resolved' ? next.state : null;
      lastAdvanceAtRef.current = seedAdvanceAt(next, now, 'reset');
      resolvedFiredRef.current = false;
      setSnapshot(next);
      setTimedOut(
        next !== null && !isTerminalBridgeFetchState(next.state) && isStale(lastAdvanceAtRef.current, now, next.state),
      );
      // Drop any cached poll result from the previous run so the next fetch is
      // the server's fresh stamp, not the run that just failed.
      void utils.productionOrders.issues.getById.invalidate({ issue_id: issueId });
    },
    [issueId, utils],
  );

  const terminal = snapshot ? isTerminalBridgeFetchState(snapshot.state) : false;
  const live = enabled && !terminal;

  // ── Polling fallback (15s) ────────────────────────────────────────────────
  const { data: polled } = trpc.productionOrders.issues.getById.useQuery(
    { issue_id: issueId },
    {
      enabled: live,
      refetchInterval: live ? BRIDGE_FETCH_POLL_MS : false,
      staleTime: 0,
    },
  );
  useEffect(() => {
    if (!polled || !enabled) return;
    if (polled.status === 'RESOLVED') {
      apply({ state: 'resolved', at: polled.resolved_at ?? null, reason: null });
      return;
    }
    const fromRow = snapshotFromMetadata(
      (polled.metadata ?? null) as Record<string, unknown> | null,
    );
    if (fromRow) apply(fromRow);
  }, [polled, enabled, apply]);

  // ── SSE (transport contract = usePrintFileEvents) ─────────────────────────
  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectAttemptRef = useRef(0);
  const lastTokenRef = useRef<string>('');
  const mountedRef = useRef(true);

  const clearReconnectTimer = useCallback(() => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
  }, []);

  const createEventSource = useCallback(
    (token: string) => {
      // Close existing connection and cancel pending reconnect.
      clearReconnectTimer();
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }

      if (!token) return;
      lastTokenRef.current = token;

      const es = new EventSource(
        `${BACKEND_URL}/events/print-files?token=${encodeURIComponent(token)}`
      );

      es.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data) as PrintFileEvent;
          reconnectAttemptRef.current = 0; // Reset backoff on any valid message.
          if (data.type === 'heartbeat') return;
          if (data.issueId !== issueId) return;
          const active = locationIdRef.current;
          if (active && data.locationId && data.locationId !== active) return;
          if (data.type === 'print_file_bridge_progress' && isBridgeFetchUiState(data.state)) {
            apply({ state: data.state, at: data.at ?? null, reason: data.reason ?? null });
            return;
          }
          if (data.type === 'print_file_issue_resolved') {
            apply({ state: 'resolved', at: data.resolvedAt ?? null, reason: null });
          }
        } catch {
          // Ignore malformed messages.
        }
      };

      es.onerror = () => {
        // Close immediately to prevent the browser's native reconnect storm.
        es.close();
        eventSourceRef.current = null;

        if (!mountedRef.current) return;
        const attempt = reconnectAttemptRef.current;
        const delay = Math.min(
          RECONNECT_BASE_MS * Math.pow(2, attempt),
          RECONNECT_MAX_MS
        );
        reconnectAttemptRef.current = attempt + 1;

        reconnectTimerRef.current = setTimeout(() => {
          if (!mountedRef.current) return;
          if (lastTokenRef.current) {
            createEventSource(lastTokenRef.current);
          }
        }, delay);
      };

      eventSourceRef.current = es;
    },
    [clearReconnectTimer, issueId, apply]
  );

  useEffect(() => {
    mountedRef.current = true;

    // Nothing to track → no stream.
    if (!live) {
      return () => {
        mountedRef.current = false;
      };
    }

    // Get the initial session token, then open the stream.
    void supabase.auth.getSession().then(({ data: { session } }) => {
      if (!mountedRef.current || !session?.access_token) return;
      createEventSource(session.access_token);
    });

    // Recreate the EventSource on token refresh (JWT expiry).
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!mountedRef.current) return;
      if (session?.access_token) {
        reconnectAttemptRef.current = 0;
        createEventSource(session.access_token);
      } else {
        clearReconnectTimer();
        if (eventSourceRef.current) {
          eventSourceRef.current.close();
          eventSourceRef.current = null;
        }
      }
    });

    return () => {
      mountedRef.current = false;
      subscription.unsubscribe();
      clearReconnectTimer();
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
    };
  }, [live, createEventSource, clearReconnectTimer]);

  // ── Staleness watchdog ────────────────────────────────────────────────────
  // Soft: it only flips the flag. The stream and the poll above are keyed on
  // `live`, not on `timedOut`, so a quiet bridge that wakes up later is still
  // heard and the flag clears on its next advance.
  useEffect(() => {
    if (!live) return;
    const tick = () => {
      if (isStale(lastAdvanceAtRef.current, Date.now(), snapshotRef.current?.state)) setTimedOut(true);
    };
    tick();
    const id = setInterval(tick, STALE_TICK_MS);
    return () => clearInterval(id);
  }, [live]);

  const failedStep =
    snapshot?.state === 'failed' ? inferFailedStep(lastProgressRef.current, snapshot.reason) : null;

  return { snapshot, failedStep, timedOut, reset };
}
